import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Bell, Check, X, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  token: string;
  workspaces?: {
    name: string;
  };
}

export const InvitationNotification: React.FC<{ 
  isGlobalDarkMode: boolean;
  align?: 'left' | 'right' | 'sidebar';
  verticalAlign?: 'top' | 'bottom';
}> = ({ isGlobalDarkMode, align = 'left', verticalAlign = 'top' }) => {
  const { user, signOut } = useAuth();
  const { refreshWorkspaces } = useWorkspace();
  const { toast } = useToast();
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPopover, setShowPopover] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const fetchInvitations = async () => {
    if (!user?.email) return;
    
    try {
      const { data, error } = await supabase
        .from('workspace_invitations')
        .select(`*, workspaces(name)`)
        .eq('email', user.email)
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString());

      if (error) throw error;
      setInvitations(data || []);
    } catch (err: any) {
      console.error('Error fetching invitations:', err);
      // Handle expired JWT by signing out
      if (err?.code === 'PGRST303' || err?.message?.includes('JWT expired')) {
        console.warn('JWT expired dectected in InvitationNotification, signing out...');
        signOut();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchInvitations();
    }
  }, [user?.email]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover]);

  const handleAction = async (invitationId: string, token: string, action: 'accept' | 'decline') => {
    setProcessingId(invitationId);
    
    try {
      if (action === 'accept') {
        const { error } = await supabase.functions.invoke('accept-invitation', {
          body: {
            action: 'accept',
            token,
            userId: user?.id
          }
        });

        if (error) {
           console.error("Function Error Payload", error);
           throw new Error(error.message || 'Error executing invitation acceptance');
        }
        
        toast({
          title: "Invitation accepted",
          description: "You have successfully joined the workspace.",
        });
        
        await refreshWorkspaces();
        
      } else if (action === 'decline') {
        const { error } = await supabase
          .from('workspace_invitations')
          .update({ status: 'declined' })
          .eq('id', invitationId);
          
        if (error) throw error;
        
        toast({
          title: "Invitation declined",
          description: "You have declined the workspace invitation.",
        });
      }
      
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      if (invitations.length <= 1) {
        setShowPopover(false);
      }
      
    } catch (error: any) {
      console.error(`Failed to ${action} invitation:`, error);
      toast({
        title: "Action failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className={`p-2 rounded-full opacity-50 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <Bell className="w-5 h-5 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="relative flex justify-center">
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            ref={buttonRef}
            onClick={() => setShowPopover(!showPopover)}
            className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
              showPopover
                ? isGlobalDarkMode
                  ? 'bg-sidebar-accent text-white'
                  : 'bg-gray-200 text-gray-900'
                : isGlobalDarkMode
                  ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Bell className="w-5 h-5" />
            {invitations.length > 0 && (
              <span className={`absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full border-2 shadow-sm ${
                isGlobalDarkMode ? 'border-sidebar' : 'border-sidebar-light'
              }`}>
                {invitations.length}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          Notifications
        </TooltipContent>
      </Tooltip>

      {showPopover && (
        <div
          ref={popoverRef}
          className={`${
            align === 'sidebar' 
              ? 'absolute bottom-0 left-[72px]'
              : `absolute ${verticalAlign === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} ${
                  align === 'right' ? 'right-0' : 'left-0'
                }`
          } w-80 rounded-xl border shadow-2xl z-50 ${
            isGlobalDarkMode
              ? 'bg-sidebar-accent border-sidebar-border'
              : 'bg-white border-gray-200'
          }`}
        >
          <div className={`px-4 py-3 border-b ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'} flex items-center justify-between`}>
            <h3 className={`font-semibold ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Notifications
            </h3>
            {invitations.length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 text-[10px] font-bold">
                {invitations.length} New
              </span>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {invitations.length === 0 ? (
              <div className="py-8 text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  isGlobalDarkMode ? 'bg-sidebar' : 'bg-gray-100'
                }`}>
                  <Bell className={`w-6 h-6 ${isGlobalDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <p className={`text-sm font-medium ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                  No new notifications
                </p>
                <p className={`text-xs mt-1 ${isGlobalDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  We'll notify you when something comes up.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {invitations.map((invite) => (
                  <div 
                    key={invite.id} 
                    className={`p-3 rounded-lg border ${
                      isGlobalDarkMode 
                        ? 'border-sidebar-border bg-sidebar' 
                        : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <div className="mb-2">
                      <p className={`text-sm font-medium ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {invite.workspaces?.name || 'A Workspace'}
                      </p>
                      <p className={`text-xs ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Role: <span className="capitalize">{invite.role}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => handleAction(invite.id, invite.token, 'accept')}
                        disabled={processingId === invite.id}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md transition-colors disabled:opacity-50"
                      >
                        {processingId === invite.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                        Accept
                      </button>
                      <button
                        onClick={() => handleAction(invite.id, invite.token, 'decline')}
                        disabled={processingId === invite.id}
                        className={`flex items-center justify-center p-1.5 rounded-md border transition-colors disabled:opacity-50 ${
                          isGlobalDarkMode
                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="Decline"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
